import { getWuiOverlaysApi } from '@jwplayer/wui-react';
import { Fragment as ReactFragment, memo, useEffect } from 'react';
import { Route as ReactRouterRoute, Switch, useLocation } from 'react-router-dom';

import { ChromeFullscreen } from '/component/chrome/fullscreen';
import { ChromeMain } from '/component/chrome/main';
import { useIsAuthenticated } from '/global-state/session/hooks';
import { Redirect } from '/module/routing/redirect';
import type { Route, RouteName } from '/module/routing/types';
import { getSentry } from '/module/sentry';
import { isDefined } from '/module/types';
import { AccessPreflight } from '/routes/_preflight/access';
import { ResourcePreflight } from '/routes/_preflight/resource';
import { SitePreflight } from '/routes/_preflight/site';
import { ROUTES } from '/routes/config';
import { NotFoundRoute } from '/routes/not-found';

type RouteGroup = { paths: string[]; routes: (Route & { routeName: RouteName })[] };

/**
 * Routes grouped by their Chrome component.
 */
export const ROUTES_GROUPED_BY_CHROME = new Map<NonNullable<Route['chrome']>, RouteGroup>();
export const AUTHENTICATED_PATHS: string[] = ['/'];
export const ROUTE_REDIRECTS: React.ReactNode[] = [];

(Object.keys(ROUTES) as (keyof typeof ROUTES)[]).forEach((routeName) => {
  const route = ROUTES[routeName];

  // Authenticated defaults to `true`
  const authenticated = 'authenticated' in route ? route.authenticated !== false : true;
  if (authenticated && route.path?.length) {
    AUTHENTICATED_PATHS.push(...route.path);
  }

  const redirectExternal = 'redirectExternal' in route ? route.redirectExternal : undefined;
  const redirectFromPath = 'redirectFromPath' in route ? route.redirectFromPath : undefined;
  if (redirectExternal && route.path?.length) {
    ROUTE_REDIRECTS.push(
      <ReactRouterRoute
        path={route.path}
        exact={true}
        component={() => {
          const redirectTo = typeof redirectExternal === 'function' ? redirectExternal() : redirectExternal;

          if (redirectTo) {
            window.location.href = redirectTo;
          }

          return null;
        }}
      />,
    );
  } else if (redirectFromPath?.length) {
    ROUTE_REDIRECTS.push(
      <ReactRouterRoute path={redirectFromPath} exact={true}>
        <Redirect to={routeName} preserveParams />
      </ReactRouterRoute>,
    );
  }

  // Default to `'main'` chrome
  const chromeName = 'chrome' in route && route.chrome ? route.chrome : 'main';

  // Create routeGroup if it doesn't exist yet
  if (!ROUTES_GROUPED_BY_CHROME.has(chromeName)) {
    ROUTES_GROUPED_BY_CHROME.set(chromeName, { paths: [], routes: [] });
  }

  const routeGroup = ROUTES_GROUPED_BY_CHROME.get(chromeName) as RouteGroup;
  routeGroup.paths = [...routeGroup.paths, ...(route.path || [])].filter(isDefined);
  routeGroup.routes.push({ ...route, routeName });
});

const WrappedRouteGroup = memo(
  ({ chromeName, routes }: { chromeName: string; routes: RouteGroup['routes'] }) => {
    routes = routes.filter((route) => route.component);

    if (!routes.length) {
      return null;
    }

    const getShortestPathLength = (paths?: string[]) => {
      return (paths || []).map((p) => p.split('/').length).sort()[0];
    };

    routes = routes.sort((a, b) => {
      return getShortestPathLength(b.path) - getShortestPathLength(a.path);
    });

    let ChromeComponent: React.ComponentType<unknown>;
    switch (chromeName) {
      case 'fullscreen':
        ChromeComponent = ChromeFullscreen;
        break;
      case 'none':
        ChromeComponent = ReactFragment;
        break;
      // This is our default chrome
      default:
        ChromeComponent = ChromeMain;
        break;
    }

    return (
      <ChromeComponent>
        <Switch>
          {routes.map((route, idx) => {
            return (
              <ReactRouterRoute key={idx} path={route.path} exact={true}>
                <WrappedRouteComponent route={route} routeName={route.routeName} />
              </ReactRouterRoute>
            );
          })}
        </Switch>
      </ChromeComponent>
    );
  },
  () => {
    // A route group can never change once initialized, so we can memoize this by always returning true
    return true;
  },
);
WrappedRouteGroup.displayName = 'WrappedRouteGroup';

const WrappedRouteComponent = memo(
  ({ route, routeName }: { route: Route; routeName: RouteName }) => {
    if (!route.component) {
      return null;
    }

    let wrappedComponent = <ResourcePreflight route={route} routeName={routeName} RouteComponent={route.component} />;

    if (route.preflightComponent) {
      wrappedComponent = <route.preflightComponent>{wrappedComponent}</route.preflightComponent>;
    }

    wrappedComponent = <AccessPreflight route={route}>{wrappedComponent}</AccessPreflight>;

    if (route.path?.find((p) => p.startsWith('/p') && p.includes(':siteId'))) {
      wrappedComponent = <SitePreflight route={route}>{wrappedComponent}</SitePreflight>;
    }

    return wrappedComponent;
  },
  () => {
    // A route can never change once initialized, so we can memoize this by always returning true
    return true;
  },
);
WrappedRouteComponent.displayName = 'WrappedRouteComponent';

export function Router() {
  const location = useLocation();
  const isAuthenticated = useIsAuthenticated();

  // biome-ignore lint/correctness/useExhaustiveDependencies: Automatically destroy open overlays when location changes
  useEffect(() => {
    const { Modal, SlideOut } = getWuiOverlaysApi();

    [...Modal.getAll(), ...SlideOut.getAll()].forEach((instance) => {
      instance.destroy();
    });

    /*
     * Every time the location changes, we start a new Sentry session
     * this will prevent session duplication for the same route when something
     * like the query params change
     */
    getSentry().then((sentry) => {
      sentry.startSession();
      sentry.captureSession();
    });

    return () => {
      // When the component unmounts, we end the Sentry session
      getSentry().then((sentry) => {
        sentry.endSession();
      });
    };
  }, [location]);

  return (
    <Switch>
      {/* Immediately redirect to login when not authorized and route requires authorization */}
      {!isAuthenticated && (
        <ReactRouterRoute path={AUTHENTICATED_PATHS} exact={true}>
          <Redirect
            to={{
              pathname: 'login',
              state: {
                from: location,
              },
            }}
          />
        </ReactRouterRoute>
      )}

      {ROUTE_REDIRECTS}

      {Array.from(ROUTES_GROUPED_BY_CHROME).map(([chromeName, routeGroup]) => {
        const { paths } = routeGroup;

        return (
          <ReactRouterRoute path={paths} exact={true} key={chromeName}>
            <WrappedRouteGroup chromeName={chromeName} routes={routeGroup.routes} />
          </ReactRouterRoute>
        );
      })}

      {/* The 404 route is set separately as the very last route that can be matched */}
      <ReactRouterRoute path="*">
        <NotFoundRoute />
      </ReactRouterRoute>
    </Switch>
  );
}
